import type { NightStop } from "../services/night-planner";
import { hoursPerDay, nightsWithPlaces, PACE_QUESTIONS, type PaceAnswers, type StayChoice } from "./aiPlanner";

/**
 * What the AI Planner (AiPlannerModal.vue) reports, so its use can be followed
 * from the hike page's button through to an applied plan. Sent with
 * window.mixpanel.track, which goes to PostHog. Pure, but for trackAiPlanner.
 */

export const AI_PLANNER_EVENTS = {
  /** The hike page's "Plan my days" button, signed in or not. */
  startClicked: 'AI Planner Start Clicked',
  opened: 'AI Planner Opened',
  stepCompleted: 'AI Planner Step Completed',
  planShown: 'AI Planner Plan Shown',
  applied: 'AI Planner Applied',
  /** Shut without applying. */
  closed: 'AI Planner Closed',
} as const;

export type AiPlannerEvent = typeof AI_PLANNER_EVENTS[keyof typeof AI_PLANNER_EVENTS];

/** Where it was started: the hike page's AI Planner section, or the planner's day toolbar. */
export type AiPlannerSource = 'hike_page' | 'day_toolbar';

export function trackAiPlanner(event: AiPlannerEvent, properties: Record<string, unknown>): void {
  window.mixpanel?.track(event, properties);
}

/** The modal's steps by name: the pace questions, then days, stay and the plan. */
export function aiPlannerStepName(step: number): string {
  if (step < PACE_QUESTIONS.length) return PACE_QUESTIONS[step].key;
  return ['days', 'stay', 'plan'][step - PACE_QUESTIONS.length] ?? 'plan';
}

export function openedProperties(source: AiPlannerSource, distanceM: number, ascentM: number) {
  return {
    source,
    distance_km: Math.round(distanceM / 100) / 10,
    ascent_m: Math.round(ascentM),
  };
}

export interface PlanSummaryInput {
  source: AiPlannerSource;
  answers: PaceAnswers;
  suggestedDays: number;
  days: number;
  stay: StayChoice | null;
  stops: NightStop[];
}

/** The plan as it stands: their answers, the days, and how the nights came out. */
export function planProperties({ source, answers, suggestedDays, days, stay, stops }: PlanSummaryInput) {
  return {
    source,
    flat: answers.flat,
    climb: answers.climb,
    hours_per_day: hoursPerDay(answers),
    suggested_days: suggestedDays,
    days,
    days_changed: days !== suggestedDays,
    stay,
    nights: stops.length,
    nights_with_places: nightsWithPlaces(stops),
    nights_chosen: stops.filter(stop => stop.pick).length,
  };
}
